import { useContext, useMemo } from 'react';

import { LumberDeckingContext } from './LumberDeckingContext';
import {
  CANTILEVER_MAX_LENGTH_INCHES,
  FORM_DEFAULT_VALUES,
  JOIST_MAXSPAN_INCHES,
  RESULT_DEFAULT_VALUES,
} from './constants';

const roundToNearestStep = (num, range) =>
  range.reduce((closest, current) => (Math.abs(current - num) < Math.abs(closest - num) ? current : closest));

const useCalculator = () => {
  const { calculatorValues, setCalculatorValues, result, setResult } = useContext(LumberDeckingContext);
  const {
    joistSize,
    joistSpacing,
    lengthFeet,
    lengthInches,
    cantileverLengthFeet,
    cantileverLengthInches,
    decking,
    additionalItems,
    widthFeet,
    widthInches,
  } = calculatorValues;
  // These values refer to the 'Additional Items' section of the calculator
  // They are not required but if one is filled out, all must be filled out
  const {
    beamSize,
    postSize,
    beamSpanFeet,
    beamSpanInches,
    spanType = 'simplespan',
    deckHeightFeet,
    deckHeightInches,
    postQuantity,
    quantityOfBeams,
  } = additionalItems;

  const convertToInches = (feet, inches) => (Number(feet) || 0) * 12 + (Number(inches) || 0);

  // Convert all measurements to inches for calculations
  const joistSpanWithoutCantilever = convertToInches(Number(lengthFeet), Number(lengthInches));
  const joistSpan =
    convertToInches(Number(cantileverLengthFeet), Number(cantileverLengthInches)) + joistSpanWithoutCantilever;
  const joistSpanWidth = convertToInches(Number(widthFeet), Number(widthInches));
  const beamSpan = convertToInches(Number(beamSpanFeet), Number(beamSpanInches));
  const postLength = convertToInches(Number(deckHeightFeet), Number(deckHeightInches));

  // Calculate overspan for joist. If true a warning will be displayed under 'Distance to beam length' input
  const joistOverspan = useMemo(
    () => !!joistSize && !!joistSpacing && joistSpanWithoutCantilever > JOIST_MAXSPAN_INCHES[joistSize][joistSpacing],
    [joistSize, joistSpacing, joistSpanWithoutCantilever]
  );

  // When main form input values are changed
  const handleFormChange = e => {
    const { name, value } = e.target;
    setCalculatorValues(prevValues => ({
      ...prevValues,
      [name]: value,
    }));
  };

  // When additional items input values are changed
  const handleAdditionalItemsChange = e => {
    const { name, value } = e.target;
    setCalculatorValues(prevValues => ({
      ...prevValues,
      additionalItems: {
        ...prevValues.additionalItems,
        [name]: value,
      },
    }));
  };

  const resetForm = () => {
    setCalculatorValues(FORM_DEFAULT_VALUES);
    setResult(RESULT_DEFAULT_VALUES);
  };

  // Convert inches to feet and inches for display purposes
  const toFeetAndInches = (inches = 0) => {
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;

    return `${feet}' ${remainingInches}"`;
  };

  // Check if additional items are required based on the values filled in
  const additionalRequired = !!(
    deckHeightFeet ||
    postSize ||
    postQuantity ||
    beamSize ||
    quantityOfBeams ||
    beamSpanFeet
  );

  // Calculate the cantilever limit based on the joist span without cantilever
  // The limit is 25% of the joist span without cantilever, but not more than 24 inches
  const cantileverLimit = useMemo(() => {
    const limit = joistSpanWithoutCantilever * 0.25;
    return limit < CANTILEVER_MAX_LENGTH_INCHES ? limit : CANTILEVER_MAX_LENGTH_INCHES;
  }, [joistSpanWithoutCantilever]);

  // Check if the cantilever length exceeds the limit
  const cantileverOverLimit = useMemo(() => {
    const cantileverLength = convertToInches(cantileverLengthFeet, cantileverLengthInches);
    return cantileverLength > cantileverLimit;
  }, [cantileverLengthFeet, cantileverLengthInches, cantileverLimit]);

  // Refers to the length of the joist based on joist span (distance to beam length + cantilever length)
  // After summing both lengths, it returns a specific length based on it
  const joistLength = useMemo(() => {
    if (joistSpan < 145) {
      return 12;
    }
    if (joistSpan <= 192) {
      return 16;
    }
    if (joistSpan <= 240) {
      return 20;
    }
    return 'Too long';
  }, [joistSpan]);

  // Refers to the length of the deck based on joist span width (Width input)
  const deckLength = useMemo(() => {
    if (joistSpanWidth <= 144) {
      return 12;
    }
    if (joistSpanWidth <= 192) {
      return 16;
    }
    if (joistSpanWidth <= 240) {
      return 20;
    }
    return 24;
  }, [joistSpanWidth]);

  // Decking width is based on the 'Decking' option selected
  const deckingWidth = useMemo(() => {
    if (decking === '1 x 6') {
      return 5.5 + 3 / 16;
    }
    return 7.5 + 3 / 16;
  }, [decking]);

  // In 'Additional Items' section, the 'Deck Height' input cannot be greater than 13 feet
  const deckOverspan = useMemo(() => postLength > 156, [postLength]);

  const joistTotal = useMemo(() => ((joistSpanWidth * joistSpan) / deckingWidth).toFixed(5), [
    joistSpanWidth,
    joistSpan,
    deckingWidth,
  ]);

  // Beam length is 'Beam Span' when 'Simple Span' is selected or double when 'Multiple Span' is selected
  const beamLength = useMemo(() => {
    if (spanType === 'simplespan') {
      return beamSpan;
    }
    return beamSpan * 2;
  }, [spanType, beamSpan]);

  // Calculated based on beam span and span type. Then it returns a specific length based on a range.
  const beamsLength = useMemo(() => {
    if (beamLength === 0) {
      return '--';
    }
    if (beamLength < 145) {
      return 12;
    }
    if (beamLength <= 192) {
      return 16;
    }
    if (beamLength <= 240) {
      return 20;
    }
    return 'Too long';
  }, [beamLength]);

  // Based on 'Deck Height' input
  // It should be a value chosen from a range of 8, 10, 12, or 16 feet based on post size
  const postsLength = useMemo(() => {
    const rawPostsLength = Math.ceil(postLength / 12) || 0;
    const range = postSize === '6x6' ? [10, 12, 16] : [8, 10, 12, 16];

    return postLength === 0 ? '--' : roundToNearestStep(rawPostsLength, range);
  }, [postLength]);

  const joistQuantity = useMemo(() => (Math.ceil(joistSpanWidth / Number(joistSpacing)) || 0) + 1, [
    joistSpanWidth,
    joistSpacing,
  ]);

  const rimQuantity = useMemo(() => Math.ceil(joistSpanWidth / (joistLength * 12)) || '--', [
    joistSpanWidth,
    joistLength,
  ]);

  const ledgerQuantity = rimQuantity;

  const deckQuantity = useMemo(() => Math.ceil((joistTotal / (deckLength * 12)) * 1.2) || '--', [
    joistTotal,
    deckLength,
  ]);

  const beamsQuantity = useMemo(() => {
    const beamSizePly = beamSize ? beamSize.split(' ')[0].replace(/\D/g, '') : 0;
    return Math.abs(Number(beamSizePly) * quantityOfBeams);
  }, [beamSize, quantityOfBeams]);

  const isFormValid = useMemo(() => {
    let additionalFieldsFilled = true;
    if (additionalRequired) {
      additionalFieldsFilled =
        beamSize && postSize && beamSpanFeet && deckHeightFeet && postQuantity && quantityOfBeams;
    }
    const joistFilled = !!(
      joistSize &&
      joistSpacing &&
      lengthFeet &&
      lengthInches &&
      decking &&
      widthFeet &&
      widthInches
    );
    return joistFilled && !!additionalFieldsFilled && !joistOverspan && !deckOverspan;
  }, [calculatorValues]);

  // joist, rim and ledger have the same size and length
  // overage is calculated as 15% of the quantity rounded up. Apart from decking which is 20%
  const calculate = () => {
    setResult({
      joist: {
        size: joistSize,
        length: joistLength,
        overage: Math.ceil(joistQuantity * 0.15),
        quantity: joistQuantity,
        total: Math.ceil(joistQuantity * 0.15) + joistQuantity,
      },
      rim: {
        size: joistSize,
        length: joistLength,
        overage: Math.ceil(rimQuantity * 0.15),
        quantity: rimQuantity,
        total: Math.ceil(rimQuantity * 0.15) + rimQuantity,
      },
      ledger: {
        size: joistSize,
        length: joistLength,
        overage: Math.ceil(ledgerQuantity * 0.15),
        quantity: ledgerQuantity,
        total: Math.ceil(ledgerQuantity * 0.15) + ledgerQuantity,
      },
      deck: {
        size: decking,
        length: deckLength,
        overage: Math.ceil(deckQuantity * 0.2),
        quantity: deckQuantity,
        total: Math.ceil(deckQuantity * 0.2) + deckQuantity,
      },
      beams: {
        size: beamSize,
        length: beamsLength,
        overage: Math.ceil(beamsQuantity * 0.15),
        quantity: beamsQuantity,
        total: Math.ceil(beamsQuantity * 0.15) + beamsQuantity,
      },
      posts: {
        size: postSize,
        length: postsLength,
        overage: Math.ceil(Number(postQuantity) * 0.15),
        quantity: Number(postQuantity),
        total: Math.ceil(Number(postQuantity) * 0.15) + Number(postQuantity),
      },
    });
  };

  return {
    calculatorValues,
    joistOverspan,
    deckOverspan,
    cantileverOverLimit,
    handleFormChange,
    handleAdditionalItemsChange,
    additionalRequired,
    resetForm,
    toFeetAndInches,
    isFormValid,
    result,
    calculate,
  };
};

export default useCalculator;
